import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';

import { AppState } from '@state';
import * as managerActions from './manager.actions';
import * as managerSelectors from './manager.selectors';
import { ManagerOfCompany, SearchProcessesOfManagedEmployees } from 'src/app/_interfaces/manager';
import { Actions, ofType } from '@ngrx/effects';
import { catchError, take, tap } from 'rxjs/operators';
import { filter, firstValueFrom, Observable, of } from 'rxjs';
import {
  CandidateProcedure,
  CandidateProcedureDecision,
  CandidateVerification,
  NewCandidateVerificationOpts,
  SearchCandidateVerificationsOpts,
  ServiceTypeOptional,
  UpdateCandidateVerificationDecisionOpts,
  UpdateCandidateVerificationDetailsOpts,
} from '@interfaces';
import { ManagerHttpService } from '@core/http/manager.http.service';

@Injectable()
export class ManagerFacade {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly actions = inject(Actions);
  private readonly managerHttpService = inject(ManagerHttpService);

  public managedCompanies$ = this.store.select(managerSelectors.selectManagedCompanies);
  public loadingManagedCompanies$ = this.store.select(managerSelectors.selectLoadingManagedCompanies);

  public processes$ = this.store.select(managerSelectors.selectProcesses);
  public processesPagination$ = this.store.select(managerSelectors.selectProcessesPagination);
  public loadingProcesses$ = this.store.select(managerSelectors.selectLoadingProcesses);
  public getMyManagedCompaniesSuccess$ = this.actions.pipe(ofType(managerActions.getMyManagedCompaniesSuccess));

  public candidateVerifications$ = this.store.select(managerSelectors.selectCandidateVerifications);
  public candidateVerificationsPagination$ = this.store.select(managerSelectors.selectCandidateVerificationsPagination);
  public loadingCandidateVerifications$ = this.store.select(managerSelectors.selectLoadingCandidateVerifications);

  public candidateVerificationDetails$ = this.store.select(managerSelectors.selectCandidateVerificationDetails);
  public loadingCandidateVerificationDetails$ = this.store.select(
    managerSelectors.selectLoadingCandidateVerificationDetails
  );
  public loadingCandidateVerificationProcedureDecision$ = this.store.select(
    managerSelectors.selectLoadingCandidateVerificationProcedureDecision
  );

  public candidateVerificationHistory$ = this.store.select(managerSelectors.selectCandidateVerificationHistory);
  public loadingCandidateVerificationHistory$ = this.store.select(
    managerSelectors.selectLoadingCandidateVerificationHistory
  );

  public getCandidateVerificationDetailsSuccess$ = this.actions.pipe(
    ofType(managerActions.getCandidateVerificationDetailsSuccess)
  );
  public getCandidateVerificationDetailsError$ = this.actions.pipe(
    ofType(managerActions.getCandidateVerificationDetailsError)
  );

  public getCandidateVerificationHistorySuccess$ = this.actions.pipe(
    ofType(managerActions.getCandidateVerificationHistorySuccess)
  );
  public getCandidateVerificationHistoryError$ = this.actions.pipe(
    ofType(managerActions.getCandidateVerificationHistoryError)
  );

  public addNewCandidateVerificationSuccess$ = this.actions.pipe(
    ofType(managerActions.addNewCandidateVerificationSuccess)
  );
  public addNewCandidateVerificationError$ = this.actions.pipe(ofType(managerActions.addNewCandidateVerificationError));

  public updateCandidateVerificationBasicDetailsSuccess$ = this.actions.pipe(
    ofType(managerActions.updateCandidateVerificationBasicDetailsSuccess)
  );
  public updateCandidateVerificationBasicDetailsError$ = this.actions.pipe(
    ofType(managerActions.updateCandidateVerificationBasicDetailsError)
  );

  public regenerateFormLinkAndPinSuccess$ = this.actions.pipe(
    ofType(managerActions.regenerateCandidateFormLinkAndPinSuccess)
  );
  public regenerateFormLinkAndPinError$ = this.actions.pipe(
    ofType(managerActions.regenerateCandidateFormLinkAndPinError)
  );

  public setCandidateVerificationDecisionSuccess$ = this.actions.pipe(
    ofType(managerActions.setCandidateVerificationDecisionSuccess)
  );
  public setCandidateVerificationDecisionError$ = this.actions.pipe(
    ofType(managerActions.setCandidateVerificationDecisionError)
  );

  public updateCandidateVerificationProcedureDecisionSuccess$ = this.actions.pipe(
    ofType(managerActions.updateCandidateVerificationProcedureDecisionSuccess)
  );
  public updateCandidateVerificationProcedureDecisionError$ = this.actions.pipe(
    ofType(managerActions.updateCandidateVerificationProcedureDecisionError)
  );

  public addCandidateOptionalServicesSuccess$ = this.actions.pipe(
    ofType(managerActions.addCandidateOptionalServicesSuccess)
  );
  public addCandidateOptionalServicesError$ = this.actions.pipe(
    ofType(managerActions.addCandidateOptionalServicesError)
  );

  public getMyManagedCompanies(): void {
    this.store.dispatch(managerActions.getMyManagedCompanies());
  }

  /**
   * Returns 'managedCompanies' from the state by doing:
   * - IF managedCompanies === null -> performs request to the backend to fetch
   * - IF managedCompanies !== null (possible empty array) -> returns mangedCompanies from the state 'as is'
   * @returns Observable<ManagerOfCompany[]> - Possible empty array!
   */
  public getMyManagedCompanies$(): Observable<ManagerOfCompany[]> {
    return this.store.select(managerSelectors.selectManagedCompanies).pipe(
      tap(companies => {
        if (!companies) {
          this.getMyManagedCompanies();
        }
      }),
      filter(companies => !!companies)
    );
  }

  public searchProcessesOfManagedEmployees(opts: SearchProcessesOfManagedEmployees): void {
    this.store.dispatch(managerActions.searchProcessesOfManagedEmployees({ opts }));
  }

  public searchCandidatesVerifications(opts: SearchCandidateVerificationsOpts): void {
    this.store.dispatch(managerActions.searchCandidatesVerifications({ opts }));
  }

  public getCandidateVerificationDetails(candidateVerificationId: string): void {
    this.store.dispatch(managerActions.getCandidateVerificationDetails({ candidateVerificationId }));
  }

  public getCandidateVerificationHistory(candidateVerificationId: string): void {
    this.store.dispatch(managerActions.getCandidateVerificationHistory({ candidateVerificationId }));
  }

  public addNewCandidateVerification(opts: NewCandidateVerificationOpts): void {
    this.store.dispatch(managerActions.addNewCandidateVerification({ opts }));
  }

  public updateCandidateVerificationBasicDetails(
    candidateVerificationId: string,
    opts: UpdateCandidateVerificationDetailsOpts
  ): void {
    this.store.dispatch(managerActions.updateCandidateVerificationBasicDetails({ candidateVerificationId, opts }));
  }

  public regenerateCandidateFormLinkAndPin(candidateVerificationId: string): void {
    this.store.dispatch(managerActions.regenerateCandidateFormLinkAndPin({ candidateVerificationId }));
  }

  public setCandidateVerificationDecision(
    candidateVerificationId: string,
    opts: UpdateCandidateVerificationDecisionOpts
  ): void {
    this.store.dispatch(managerActions.setCandidateVerificationDecision({ candidateVerificationId, opts }));
  }

  public updateCandidateVerificationProcedureDecision(
    candidateVerificationId: string,
    procedureId: string,
    decision: CandidateProcedureDecision
  ): void {
    this.store.dispatch(
      managerActions.updateCandidateVerificationProcedureDecision({ candidateVerificationId, procedureId, decision })
    );
  }

  public addCandidateOptionalServices(candidateVerificationId: string, services: ServiceTypeOptional[]): void {
    this.store.dispatch(managerActions.addCandidateOptionalServices({ candidateVerificationId, services }));
  }
}
